/**
 * Stagger (W2-11 batch-tools; DESIGN.md §5.8): shift each selected item by an increasing amount.
 *
 * Notes and keys are taken together in time order and grouped by beat (a chord, or the X and Y keys of one moment,
 * move as one item); group g (0-based) moves by g * step. X/Y-linked partner keys at the same beat move with their
 * key. Holds keep their length. Nothing may move before beat 0, and keys keep their order on their tracks (a key
 * that would land on or pass an unmoved neighbour fails the whole operation with an explanation).
 */

import type { KeyId, Note, NoteId, TrackId } from '../model/types';
import type { TxBuilder } from '../ops/types';
import { Beats } from '../time/beat';
import type { Beat } from '../time/types';
import { commitKeys, readKeys, requireTrack, withXyPartners, type KeyRec } from '../commands/kit';
import { EarliestBeat, toolReason, TOOLS_MSG } from './common';
import type { ToolReport } from './types';

export interface StaggerParams {
  readonly notes: readonly NoteId[];
  readonly keys: readonly KeyId[];
  readonly step: Beat;
}

/** Shift of every distinct beat among `beats` (sorted ascending): the g-th distinct beat moves by g * step. */
export function staggerShifts(beats: readonly Beat[], step: Beat): Map<string, Beat> {
  const distinct = new Map<string, Beat>();
  for (const b of beats) distinct.set(Beats.key(b), b);
  const sorted = [...distinct.values()].sort((a, b) => Beats.cmp(a, b));
  const out = new Map<string, Beat>();
  sorted.forEach((b, g) => out.set(Beats.key(b), Beats.mulInt(step, g)));
  return out;
}

export function stagger(tx: TxBuilder, p: StaggerParams): ToolReport {
  if (Beats.eq(p.step, Beats.ZERO)) tx.fail(toolReason(TOOLS_MSG.staggerZero));
  const notes: Note[] = [];
  const seenNotes = new Set<NoteId>();
  for (const id of p.notes) {
    const n = tx.doc.notes.get(id);
    if (n !== undefined && !seenNotes.has(id)) {
      seenNotes.add(id);
      notes.push(n);
    }
  }
  const keyIds = withXyPartners(
    tx,
    p.keys.filter((id) => tx.doc.keys.has(id)),
  );
  if (notes.length === 0 && keyIds.length === 0) tx.fail(toolReason(TOOLS_MSG.staggerNothing));

  const beats: Beat[] = notes.map((n) => n.t);
  for (const id of keyIds) {
    const k = tx.doc.keys.get(id);
    if (k !== undefined) beats.push(k.t);
  }
  const shifts = staggerShifts(beats, p.step);
  if (shifts.size < 2) tx.fail(toolReason(TOOLS_MSG.staggerSingle));
  const shiftOf = (b: Beat): Beat => shifts.get(Beats.key(b)) ?? Beats.ZERO;

  let changed = 0;
  const focus = new EarliestBeat();
  const touch = (b: Beat): void => {
    focus.add(b);
  };

  for (const n of notes) {
    const dt = shiftOf(n.t);
    if (Beats.eq(dt, Beats.ZERO)) continue;
    const t = Beats.add(n.t, dt, tx.rounding);
    if (Beats.cmp(t, Beats.ZERO) < 0) tx.fail(toolReason(TOOLS_MSG.retimeBeforeStart));
    const end = n.kind === 'hold' ? Beats.add(n.end, dt, tx.rounding) : t;
    tx.update('notes', n.id, { t, end });
    changed++;
    touch(t);
  }

  const byTrack = new Map<TrackId, Set<KeyId>>();
  for (const id of keyIds) {
    const k = tx.doc.keys.get(id);
    if (k === undefined) continue;
    const set = byTrack.get(k.track);
    if (set === undefined) byTrack.set(k.track, new Set([id]));
    else set.add(id);
  }
  for (const [trackId, ids] of byTrack) {
    const info = requireTrack(tx, trackId);
    const keys = readKeys(tx, trackId);
    let moved = false;
    for (const k of keys) {
      if (k.id === null || !ids.has(k.id)) continue;
      const dt = shiftOf(k.t);
      if (Beats.eq(dt, Beats.ZERO)) continue;
      const t = Beats.add(k.t, dt, tx.rounding);
      if (Beats.cmp(t, Beats.ZERO) < 0) tx.fail(toolReason(TOOLS_MSG.retimeBeforeStart));
      k.t = t;
      moved = true;
      changed++;
      touch(t);
    }
    if (!moved) continue;
    for (let i = 1; i < keys.length; i++) {
      if (!Beats.lt((keys[i - 1] as KeyRec).t, (keys[i] as KeyRec).t)) tx.fail(toolReason(TOOLS_MSG.retimeCollide));
    }
    commitKeys(tx, info, keys);
  }
  const line = notes[0]?.line;
  if (focus.beat !== null) tx.setFocus(line === undefined ? { beat: focus.beat } : { beat: focus.beat, line });
  return { changed };
}
